import { useCallback } from "react";

// This object lives at the top level of the file (module level), which means
// it stays in memory for the whole time the page is open. It is emptied
// automatically when the user reloads the page, because the file is loaded
// fresh on every page load. We use it to remember each chat's messages while
// the chat is closed, so the history comes back when the chat is reopened.
const savedConversations = {};

/**
 * Remembers a chat conversation in memory so it survives the chat being
 * closed and reopened. The memory is cleared when the page is reloaded.
 *
 * Each chat is kept separate by its own key (we use the dashboard type),
 * so different dashboards never share their history.
 *
 * @param {string} conversationKey - A unique name for this chat (for example, the dashboard type)
 * @returns {{ savedMessages: Array, saveMessages: Function }}
 */
export const useConversationMemory = (conversationKey) => {
	// Get whatever we saved for this key last time. If we have not saved
	// anything yet, start with an empty list.
	const savedMessages = savedConversations[conversationKey] || [];

	// Save the latest messages for this key, so they are ready the next time
	// the chat is opened. We wrap this in useCallback so the function keeps the
	// same identity between renders, which keeps the saving effect stable.
	const saveMessages = useCallback(
		(messages) => {
			savedConversations[conversationKey] = messages;
		},
		[conversationKey],
	);

	return { savedMessages, saveMessages };
};
